import { CheckCircleOutlined, DisconnectOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useCallback, useState } from 'react';

import { useAutoInventoryRooms } from 'app/hooks/data/core/extranet/inventory/rooms/useAutoInventoryRooms';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { ConnectivityToStaycation } from '../_components/ConnectivityToStaycation/ConnectivityToStaycation';

import styles from './StepAutoInventory.module.scss';

type StepAutoInventoryProps = {
  hotelId: number;
  onClose: () => void;
  onStartSync: () => void;
};

export const StepAutoInventory = ({
  hotelId,
  onClose,
  onStartSync,
}: StepAutoInventoryProps) => {
  const [isSyncFailed, setIsSyncFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { getAutoInventoryRooms } = useAutoInventoryRooms();

  const handleStartSync = useCallback(async () => {
    amplitudeTrack('Click Start configuration');
    setIsLoading(true);

    try {
      await getAutoInventoryRooms(hotelId);

      onStartSync();
    } catch {
      setIsSyncFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [getAutoInventoryRooms, hotelId, onStartSync]);

  if (isSyncFailed) {
    return <SyncFailedView onClose={onClose} />;
  }

  return <ExplanationView onStart={handleStartSync} isLoading={isLoading} />;
};

type ExplanationViewProps = {
  onStart: () => void;
  isLoading: boolean;
};

const ExplanationView = ({ onStart, isLoading }: ExplanationViewProps) => (
  <div className={styles.stepAutoInventory}>
    <div className={styles.header}>
      <ConnectivityToStaycation />
      <div className={styles.headerText}>
        <div className={styles.title}>AUTO INVENTORY</div>
        <div className={styles.subtitle}>
          Simplify room inventory management with Booking.com
        </div>
      </div>
    </div>

    <div className={styles.content}>
      <div className={styles.argumentList}>
        <p>Auto inventory tool will give us access to:</p>
        <div className={styles.checkList}>
          <div className={styles.checkItem}>
            <CheckCircleOutlined />{' '}
            <div>
              <span className={styles.highlight}>Room categories</span>{' '}
              published on Booking.com
            </div>
          </div>
          <div className={styles.checkItem}>
            <CheckCircleOutlined />{' '}
            <div>
              <span className={styles.highlight}>Available stock</span> for sale
              on Booking.com
            </div>
          </div>
          <div className={styles.checkItem}>
            <CheckCircleOutlined />{' '}
            <div>
              <span className={styles.highlight}>OTA rate</span> published on
              Booking.com
            </div>
          </div>
        </div>
      </div>

      <div className={styles.argumentList}>
        <p>It will take less than 3 minutes.</p>
      </div>
    </div>
    <div className={styles.footer}>
      <Button type="primary" loading={isLoading} onClick={onStart}>
        Start configuration
      </Button>
      {isLoading && (
        <p className={styles.loadingHint}>
          This action could take a few seconds, please wait...
        </p>
      )}
    </div>
  </div>
);

type SyncFailedViewProps = {
  onClose: () => void;
};

const SyncFailedView = ({ onClose }: SyncFailedViewProps) => {
  const handleContactAM = () => {
    onClose();
  };

  return (
    <div className={styles.stepAutoInventory}>
      <div className={styles.header}>
        <div className={styles.failIcon}>
          <DisconnectOutlined />
        </div>
        <div className={styles.headerText}>
          <p className={styles.failTitle}>Configuration failed...</p>
          <p className={styles.failText}>
            We were unable to fetch your Booking.com information, please contact
            your AM for more details.
          </p>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.buttons}>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" onClick={handleContactAM}>
            Contact AM
          </Button>
        </div>
      </div>
    </div>
  );
};
